import { normalizeHeadingDepth } from '@heidi/answer-kit';
import { useCallback, useMemo, type ComponentType } from 'react';

import type { ChatItem } from '../../../contracts/harness.types';
import type { RefTarget } from '../../../contracts/projects/ref-tags';
import type { ProjectFeedbackRating } from '../../../contracts/projects/requests.types';
import { useSubmitProjectFeedback } from '../../../data/projects/hooks';
import { extractLatestTurnCopyableText } from '../../../data/projects/lib/extractLatestTurnCopyableText';
import type { ProjectMessageActionsProps } from './ProjectMessageActions';
import {
  ResponseActionsReveal,
  type ResponseActionsVisibility,
} from './ResponseActionsReveal';

export interface ProjectReplyMessageActionsProps {
  projectId: string;
  /** Items of the turn being rated; copy text is taken from its latest turn. */
  displayItems: ChatItem[];
  /** Overrides the parent-turn prose, e.g. with an Evidence child's answer. */
  copyableText?: string | null;
  /** Transcript position of the reply, sent as the feedback `message_id`. */
  messageId: string;
  /** Ratings given this tab, keyed `projectId:messageId`. The row remounts
   *  when its reply stops being the latest (tail -> turn footer) or the chat is
   *  reopened, and the backend doesn't echo ratings back, so they're remembered. */
  givenRatings: Map<string, ProjectFeedbackRating>;
  visibility?: ResponseActionsVisibility;
  hideFeedback?: boolean;
}

/**
 * What the app supplies: the action row bound to its message-actioned emit, the
 * name a reference's chip resolved from the chat-object cache, and the pass that
 * strips Evidence's citation and image markup, which Evidence owns. Pass stable
 * values (a component, a cache reader, a module-level function).
 */
export interface ProjectReplyMessageActionsHost {
  MessageActions: ComponentType<ProjectMessageActionsProps>;
  labelFor: (target: RefTarget) => string | undefined;
  stripCitationMarkup: (text: string) => string;
}

/**
 * A Work reply's action row: resolves its copyable text, submits per-message
 * feedback and remembers the given rating across remounts, inside the
 * hover-reveal wrapper.
 */
export const ProjectReplyMessageActions = ({
  projectId,
  displayItems,
  copyableText,
  messageId,
  givenRatings,
  visibility = 'always',
  hideFeedback = false,
  MessageActions,
  labelFor,
  stripCitationMarkup,
}: ProjectReplyMessageActionsProps & ProjectReplyMessageActionsHost) => {
  const submitFeedback = useSubmitProjectFeedback(projectId);
  const ratingKey = `${projectId}:${messageId}`;
  const resolvedCopyableText = useMemo(
    () =>
      copyableText !== undefined
        ? copyableText
        : extractLatestTurnCopyableText(displayItems, {
            labelFor,
            stripCitationMarkup,
            normalizeHeadingDepth,
          }),
    [copyableText, displayItems, labelFor, stripCitationMarkup]
  );

  const handleMessageFeedback = useCallback(
    async (rating: ProjectFeedbackRating, comment?: string) => {
      const response = await submitFeedback.mutateAsync({
        rating,
        message_id: messageId,
        user_feedback: comment ?? null,
      });
      givenRatings.set(ratingKey, rating);
      return response;
    },
    [submitFeedback, messageId, givenRatings, ratingKey]
  );

  return (
    <ResponseActionsReveal
      visibility={visibility}
      className="duration-300 animate-in fade-in"
    >
      <MessageActions
        key={messageId}
        projectId={projectId}
        copyableText={resolvedCopyableText}
        onSubmitFeedback={handleMessageFeedback}
        hideFeedback={hideFeedback}
        initialSelected={givenRatings.get(ratingKey) ?? null}
      />
    </ResponseActionsReveal>
  );
};
